import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of, switchMap } from 'rxjs';

/** The brand part of every browser tab title — the same text index.html shows before the app boots. */
export const APP_TITLE = 'H Extranet';

/** "Reservations · H Extranet"; the brand alone when a route has no title. */
export function formatPageTitle(page: string | null | undefined): string {
  const name = page?.trim();
  return name ? `${name} · ${APP_TITLE}` : APP_TITLE;
}

/**
 * A browser tab title per page (WI 3787): the deepest active route's `title` is an i18n key, shown
 * in the UI language as "<page> · H Extranet", and re-translated when the language changes — the
 * tab used to read "H Extranet" on every page, so history, bookmarks and open tabs were
 * indistinguishable.
 *
 * `translate.stream` waits for the language file on the first load and emits again on every
 * language switch; `switchMap` drops the previous page's subscription on each navigation.
 */
@Injectable()
export class PageTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly titleKey = new BehaviorSubject<string | undefined>(undefined);

  constructor() {
    super();
    this.titleKey.pipe(
      switchMap(key => (key ? this.translate.stream(key) : of(null))),
      takeUntilDestroyed(),
    ).subscribe((page: string | null) => this.title.setTitle(formatPageTitle(page)));
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.titleKey.next(this.buildTitle(snapshot));
  }
}
